import React from 'react';

const ProductUnits: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Product Units</h1>
          <p className="text-gray-600 mt-2">
            The Product Units page in DAS allows you to define and manage units of measurement for your products. Set up standardized units with proper abbreviations and precision levels to ensure consistent product management across your inventory, sales, and purchasing operations.
          </p>
        </div>

        {/* Main Product Units List Image */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-6">
            <img
              src="/images/product-units/list.png"
              alt="Product Units List Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
          </div>
          <p className="text-gray-600 text-sm">
            <strong>Product Units Overview:</strong> The main interface showing all defined units with their names and abbreviations. Common units like Kilogram (Kg), Liter (L), and Package (Pcs) are displayed in a clean table format.
          </p>
        </div>

        {/* Product Units Overview Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Product Units Overview</h2>
          <p className="text-gray-600 mb-4">
            The Product Units page provides a comprehensive view of all measurement units available in your system. These units are used throughout DAS for product definitions, inventory tracking, sales transactions, and purchase orders.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Units Table Structure</h3>
          <p className="text-gray-600 mb-4">The units table displays the following information:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Name:</strong> The full name of the unit (e.g., "Kilogram", "Liter", "Package")</li>
            <li><strong>Abbreviation:</strong> The short form used in displays and reports (e.g., "Kg", "L", "Pcs")</li>
            <li><strong>Actions:</strong> Quick access menu for editing or managing units</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Common Unit Types</h3>
          <p className="text-gray-600 mb-4">Based on your current setup, typical unit categories include:</p>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Weight Units:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Kilogram (Kg)</li>
                <li>Gram (g)</li>
                <li>Pound (lb)</li>
                <li>Ounce (oz)</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Volume Units:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Liter (L)</li>
                <li>Milliliter (ml)</li>
                <li>Gallon (gal)</li>
                <li>Fluid Ounce (fl oz)</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Count Units:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Package (Pcs)</li>
                <li>Piece (pc)</li>
                <li>Dozen (dz)</li>
                <li>Box (box)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Unit Management Actions Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Unit Management Actions</h2>
          <p className="text-gray-600 mb-4">
            Each unit row includes an actions menu providing management options:
          </p>

          <div className="mb-6">
            <img
              src="/images/product-units/list-buttons.png"
              alt="Unit Actions Menu"
              className="w-full max-w-2xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Unit Actions:</strong> The dropdown menu appears when clicking the three dots icon, providing access to Edit, Mark As Inactive, and Delete options for managing units.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Available Actions</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Edit:</strong> Modify unit name, abbreviation, and precision settings</li>
            <li><strong>Mark As Inactive:</strong> Deactivate units without deleting them</li>
            <li><strong>Delete:</strong> Permanently remove units (with confirmation)</li>
          </ul>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Deleting a unit that is assigned to products will affect those product definitions. Consider marking units as inactive instead to preserve historical data.
            </p>
          </div>
        </div>

        {/* Creating New Units Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Product Units</h2>
          <p className="text-gray-600 mb-4">
            Adding new units is straightforward using the <strong>+ New</strong> button in the Product Units page.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Create Unit Process</h3>
          <p className="text-gray-600 mb-4">To create a new product unit:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the <strong>+ New</strong> button in the top-right corner</li>
            <li>The "New Unit" dialog will open with the following fields:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/product-units/form.png"
              alt="Create Unit Dialog"
              className="w-full max-w-3xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>New Unit Form:</strong> Modal dialog with empty fields for Name, Abbreviation, and Precision dropdown for creating new measurement units.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Form Fields</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Name:</strong> Enter the full unit name (required field) - e.g., "Kilogram", "Meter", "Dozen"</li>
            <li><strong>Abbreviation:</strong> Enter the short form (required field) - e.g., "Kg", "m", "dz"</li>
            <li><strong>Precision:</strong> Select decimal precision from dropdown (0-6 decimal places)</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Precision Settings</h4>
          <p className="text-gray-600 mb-4">The precision setting determines how many decimal places are allowed for quantities:</p>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Precision Examples:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>0 decimals:</strong> Whole numbers only (e.g., 5 pieces)</li>
                <li><strong>1 decimal:</strong> One decimal place (e.g., 2.5 kg)</li>
                <li><strong>2 decimals:</strong> Two decimal places (e.g., 1.25 liters)</li>
                <li><strong>3+ decimals:</strong> High precision (e.g., 0.125 grams)</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Recommended Precision:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Count units:</strong> 0 decimals (pieces, boxes)</li>
                <li><strong>Weight units:</strong> 2-3 decimals (kg, g)</li>
                <li><strong>Volume units:</strong> 2-3 decimals (L, ml)</li>
                <li><strong>Length units:</strong> 2-3 decimals (m, cm)</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> Choose precision based on how you typically measure and sell your products. Higher precision allows for more accurate measurements but may complicate simple transactions.
            </p>
          </div>

          <p className="text-gray-600 mb-4">After filling the form:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Review the unit name, abbreviation, and precision setting</li>
            <li>Click <strong>Save</strong> to create the unit</li>
            <li>Click <strong>Cancel</strong> to discard and close the dialog</li>
            <li>The new unit will appear in the units list and be available for product assignment</li>
          </ol>
        </div>

        {/* Editing Units Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Editing Product Units</h2>
          <p className="text-gray-600 mb-4">
            Modify existing units to update names, abbreviations, or precision settings.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Edit Unit Process</h3>
          <p className="text-gray-600 mb-4">To edit an existing unit:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the actions menu (three dots) for the unit you want to edit</li>
            <li>Select <strong>Edit</strong> from the dropdown menu</li>
            <li>The "Edit Unit" dialog will open with current information pre-filled:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/product-units/edit.png"
              alt="Edit Unit Dialog"
              className="w-full max-w-3xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Edit Unit Form:</strong> Modal dialog showing "Liter" being edited with abbreviation "L" and precision set to "0" in the dropdown, demonstrating the edit functionality.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Editable Properties</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Unit Name:</strong> Update the full display name of the unit</li>
            <li><strong>Abbreviation:</strong> Modify the short form used in displays</li>
            <li><strong>Precision:</strong> Change the number of decimal places allowed</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Common Edit Scenarios</h4>
          <div className="space-y-3 mb-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-800">Standardizing Names</p>
              <p className="text-gray-600 text-sm">Update unit names to follow consistent naming conventions across your system.</p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <p className="font-semibold text-gray-800">Adjusting Precision</p>
              <p className="text-gray-600 text-sm">Modify precision to match how you actually measure and track products in your business.</p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <p className="font-semibold text-gray-800">Correcting Abbreviations</p>
              <p className="text-gray-600 text-sm">Fix abbreviations to match industry standards or your preferred format.</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Changes to precision will affect how quantities are displayed and entered for products using this unit. Existing product quantities will be adjusted to match the new precision setting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductUnits;
